import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icon, SearchBar, Text, Tooltip, theme } from 'components';
import styled from 'styled-components';
import moment from 'moment';
import { LockboxesActivityFilterOptions } from 'api/get/getListingAccessActivities';
import DateRangePicker from '../ShowingsCalendarPage/DateRangePicker';
import { getSupraLockboxActivitiesLink } from '../../../api/SupraLockboxController/getSupraLockboxActivities';

type QuickLockboxesActivityRightHeaderProps = {
  filters: LockboxesActivityFilterOptions;
  accessibleUserIds: string[];
  sort: any;
  page: number;
  isLoading: boolean;
  total: number;
  initialTotal: number;
  searchText: string;
  setSearchText: (text: string) => void;
  setFilters: (filters: LockboxesActivityFilterOptions) => void;
  setPage: (page: number) => void;
};

const QuickLockboxesActivityRightHeader: React.FC<
  QuickLockboxesActivityRightHeaderProps
> = ({
  filters,
  accessibleUserIds,
  sort,
  page,
  isLoading,
  total,
  initialTotal,
  searchText,
  setSearchText,
  setFilters,
  setPage,
}) => {
  const { startDate, endDate } = filters;

  const date: [moment.Moment, moment.Moment] | undefined | null = startDate &&
    endDate && [moment(startDate), moment(endDate)];

  const onDateChange = (data: [moment.Moment, moment.Moment]) => {
    setPage(1);
    if (!data[0] && !data[1]) {
      return setFilters({
        ...filters,
        startDate: null,
        endDate: null,
      });
    }
    // eslint-disable-next-line no-underscore-dangle
    const _startDate = data[0]
      ? data[0].startOf('day').toDate()
      : filters.startDate;
    // eslint-disable-next-line no-underscore-dangle
    const _endDate = data[1] ? data[1].endOf('day').toDate() : filters.endDate;
    setFilters({
      ...filters,
      startDate: _startDate,
      endDate: _endDate,
    });
  };

  const assembleFilters = useMemo(() => {
    const searchFilters: LockboxesActivityFilterOptions = { ...filters };
    if (!filters.userIds?.length) {
      searchFilters.userIds = accessibleUserIds;
    }
    if (!filters.activities?.length) {
      searchFilters.activities = ['assignee_changed', 'owner_changed'];
    }
    return searchFilters;
  }, [filters, accessibleUserIds]);

  const csvLink = useMemo(
    () =>
      getSupraLockboxActivitiesLink({
        filters: assembleFilters,
        sort,
        page,
        exportCsvType: 'quickLockbox',
      }),
    [page, sort, assembleFilters],
  );

  return (
    <StyledContainer>
      {!isLoading && (
        <StyledText data-count={total} data-total={initialTotal}>
          {total} of {initialTotal} activities
        </StyledText>
      )}
      <StyledDateRangePicker
        value={date}
        onChange={onDateChange}
        data-cy="lockboxes-activity-date-picker"
        disableFutureDates
        disabled={isLoading}
      />
      <SearchBar
        onChange={(value: string) => {
          setPage(1);
          setSearchText(value);
        }}
        placeholder={`Search by activity, user, serial #`}
        dataCy="lockboxes-activity-search-bar"
        shape="round"
        value={searchText}
        allowClear
        labelWidth={360}
      />
      <Tooltip
        title="Can only export up to 50 pages from the current page."
        placement="topRight"
        data-cy="lockboxes-activity-tooltip"
      >
        <StyledButton
          data-cy="export-button"
          href={csvLink}
          rel="noopener noreferrer"
          target="_blank"
        >
          Download .csv
        </StyledButton>
      </Tooltip>
    </StyledContainer>
  );
};

export default QuickLockboxesActivityRightHeader;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 5px 12px 5px 12px;
  overflow-x: auto;
  overflow-y: none;
`;

const StyledDateRangePicker = styled(DateRangePicker)`
  min-width: 200px;
`;

const StyledText = styled(Text)`
  color: ${theme.textColorSecondary};
  font-size: 14px;
  word-break: normal;
`;

const StyledButton = styled(Button)`
  color: white;
  background-color: ${theme.blue6};
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 3px !important;
  font-size: 14px;
`;
